<template>
  <div class="px-4 sm:px-0">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold tracking-tight text-gray-900">{{ $gettext("Clients") }}</h2>
        <p class="mt-1 text-sm text-gray-500">{{ $gettext("Voici un résumé de l'ensemble des clients.") }}</p>
      </div>
      <div v-if="customerStore.totalCount > 0 || customerStore.searchQuery" class="flex items-center gap-3">
        <router-link :to="{name: 'new-company'}"
          class="inline-flex items-center gap-x-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <PlusIcon class="-ml-0.5 size-4" aria-hidden="true" />
          {{ $gettext("Organisation") }}
        </router-link>
        <router-link :to="{name: 'new-physical-person'}"
          class="inline-flex items-center gap-x-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <PlusIcon class="-ml-0.5 size-4" aria-hidden="true" />
          {{ $gettext("Personne physique") }}
        </router-link>
      </div>
    </div>

    <div class="mt-6 flow-root">
      <!-- Empty state -->
      <div v-if="customerStore.totalCount == 0 && !customerStore.searchQuery" class="text-center p-20">
        <div class="flex justify-center items-center">
          <NewCustomerIcon />
        </div>
        <h3 class="mt-2 text-sm font-semibold text-gray-900">{{ $gettext("Pas encore de clients") }}</h3>
        <p class="mt-1 text-sm text-gray-500">{{ $gettext("Désolé, mais nous ne trouvons pas de clients... Veuillez ajouter un nouveau client.") }}</p>
        <div class="mt-6 flex justify-center gap-3">
          <router-link :to="{name: 'new-company'}"
            class="inline-flex items-center gap-x-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors">
            <PlusIcon class="-ml-0.5 size-4" aria-hidden="true" />
            {{ $gettext("Nouvelle organisation") }}
          </router-link>
          <router-link :to="{name: 'new-physical-person'}"
            class="inline-flex items-center gap-x-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors">
            <PlusIcon class="-ml-0.5 size-4" aria-hidden="true" />
            {{ $gettext("Nouvelle personne physique") }}
          </router-link>
        </div>
      </div>

      <!-- List -->
      <div v-else>
        <!-- Search + count -->
        <div class="flex items-center justify-between mb-4">
          <div class="relative w-80">
            <MagnifyingGlassIcon class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-4" />
            <input v-model="customerStore.searchQuery" type="text" :placeholder="$gettext('Chercher un client...')"
              class="block w-full rounded-lg border-0 py-2 pl-9 pr-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500" />
          </div>
          <span class="text-sm text-gray-500">{{ customerStore.totalCount }} {{ $gettext("clients") }}</span>
        </div>

        <!-- Table card -->
        <div class="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5">
          <table v-if="customerStore.customers.length > 0" class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="py-3 pl-6 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Nom") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Profil de risque") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Vente(s)") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{{ $gettext("Location(s)") }}</th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"></th>
                <th scope="col" class="relative py-3 pl-3 pr-6"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="customer in customerStore.paginatedCustomers"
                :key="customer.id"
                class="hover:bg-gray-50 transition-colors cursor-pointer"
                @click="$router.push({ name: customer.type === 'company' ? 'company' : 'physical-person', params: { id: customer.id } })"
              >
                <td class="whitespace-nowrap py-3.5 pl-6 pr-3 text-sm font-medium text-gray-900">
                  <CustomerLink :customer="customer" @click.stop />
                </td>
                <td class="whitespace-nowrap px-3 py-3.5 text-sm text-gray-500">
                  <RiskScoringLabel :level="customer.relationships?.lastRiskScoring?.data?.attributes?.scoreLevel ?? undefined" />
                </td>
                <td class="whitespace-nowrap px-3 py-3.5 text-sm text-gray-500">
                  {{ customer.relationships?.saleListingAgreements?.count ?? customer.relationships?.saleListingAgreements?.data?.length }}
                </td>
                <td class="whitespace-nowrap px-3 py-3.5 text-sm text-gray-500">
                  {{ customer.relationships?.rentalListingAgreements?.count ?? customer.relationships?.rentalListingAgreements?.data?.length }}
                </td>
                <td class="whitespace-nowrap px-3 py-3.5 text-sm text-gray-500">
                  <span v-if="customer.attributes?.isUboOnly || false" class="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-500/10">{{ $gettext("UBO uniquement") }}</span>
                </td>
                <td class="relative whitespace-nowrap py-3.5 pl-3 pr-6 text-right text-sm font-medium">
                  <div class="flex justify-end gap-2">
                    <router-link
                      v-if="!(customer.attributes?.isUboOnly || false)"
                      class="rounded-md p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                      :to="{ name: customer.type === 'company' ? 'company' : 'physical-person', params: { id: customer.id } }"
                      @click.stop
                    >
                      <MagnifyingGlassIcon class="size-4" aria-hidden="true" />
                    </router-link>
                    <router-link
                      class="rounded-md p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                      :to="{ name: customer.type === 'company' ? 'edit-company' : 'edit-physical-person', params: { id: customer.id } }"
                      @click.stop
                    >
                      <PencilSquareIcon class="size-4" aria-hidden="true" />
                    </router-link>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-center py-20">
            <h3 class="text-sm font-semibold text-gray-900">{{ $gettext("Pas de résultats trouvés") }}</h3>
          </div>
        </div>
        <PaginationNavigation :store="customerStore" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from "vue";
import NewCustomerIcon from "@/components/assets/NewCustomerIcon.vue";
import { PlusIcon } from "@heroicons/vue/20/solid";
import { MagnifyingGlassIcon, PencilSquareIcon } from "@heroicons/vue/24/outline";
import { useCustomerStore } from "@/stores/customer.js";
import PaginationNavigation from "@/components/layout/PaginationNavigation.vue";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import CustomerLink from "@/components/customers/CustomerLink.vue";

const customerStore = useCustomerStore();

const customerOverviewIncludes = ["lastRiskScoringLevel", "saleListingAgreementsCount", "rentalListingAgreementsCount"];

onMounted(async () => {
  await customerStore.fetchAll(null, customerOverviewIncludes);
  await customerStore.watchSourceChanges(() => {
    customerStore.fetchAll(null, customerOverviewIncludes);
  });
});
</script>
